import { z } from 'zod';
const nonEmptyStringSchema = z.string().trim().min(1);
const timestampSchema = z.string().trim().min(1);
const idStringSchema = z.string().trim().min(1);
const positiveIntSchema = z.number().int().positive();
const nonNegativeIntSchema = z.number().int().min(0);
const basisPointsSchema = z.number().int().min(0).max(10000);
const jsonObjectSchema = z.record(z.string(), z.unknown());
export const verifierStateSchema = z.enum([
    'pending',
    'unverified',
    'verified_no_arb',
    'verified_arb',
]);
export const unverifiedReasonSchema = z.enum([
    'missing_mapping',
    'missing_equivalent_odds',
    'provider_timeout',
    'provider_error',
    'stale_odds',
    'unsupported_odds_phase',
]);
export const equivalenceKindSchema = z.enum(['equivalent', 'derived']);
export const oddsPhaseSchema = z.enum(['pre_match', 'live']);
export const edgeDirectionSchema = z.enum([
    'polymarket_overpriced',
    'polymarket_underpriced',
    'even',
]);
export const footballFixtureSchema = z.object({
    id: idStringSchema,
    apiSportsFixtureId: positiveIntSchema,
    rolloutGroup: nonEmptyStringSchema,
    competitionLabel: nonEmptyStringSchema,
    fixtureStartTime: timestampSchema,
    homeTeamLabel: nonEmptyStringSchema,
    awayTeamLabel: nonEmptyStringSchema,
    enabled: z.boolean().default(true),
    archivedAt: timestampSchema.nullable().default(null),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const createFootballFixtureSchema = footballFixtureSchema
    .omit({
    id: true,
    archivedAt: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    enabled: z.boolean().default(true),
});
export const updateFootballFixtureSchema = createFootballFixtureSchema.partial();
export const fixtureMappingSchema = z.object({
    id: idStringSchema,
    reviewedEquivalenceId: idStringSchema.nullable().optional(),
    footballFixtureId: idStringSchema,
    polymarketMarketId: nonEmptyStringSchema,
    polymarketConditionId: nonEmptyStringSchema.nullable().optional(),
    polymarketOutcomeId: nonEmptyStringSchema,
    polymarketOutcomeLabel: nonEmptyStringSchema,
    polymarketOutcomeIndex: nonNegativeIntSchema,
    polymarketClobTokenId: nonEmptyStringSchema,
    apiSportsBookmakerId: positiveIntSchema.nullable(),
    apiSportsBookmakerLabel: nonEmptyStringSchema.nullable(),
    apiSportsBetId: positiveIntSchema,
    apiSportsBetLabel: nonEmptyStringSchema,
    apiSportsValue: nonEmptyStringSchema,
    equivalenceKind: equivalenceKindSchema,
    oddsPhase: oddsPhaseSchema,
    enabled: z.boolean().default(true),
    archivedAt: timestampSchema.nullable().default(null),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
const fixtureMappingWriteSchema = fixtureMappingSchema
    .omit({
    reviewedEquivalenceId: true,
    id: true,
    archivedAt: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    enabled: z.boolean().default(true),
});
export const createFixtureMappingSchema = fixtureMappingWriteSchema.superRefine((mapping, ctx) => {
    if ((mapping.apiSportsBookmakerId === null) !== (mapping.apiSportsBookmakerLabel === null)) {
        ctx.addIssue({
            code: 'custom',
            path: ['apiSportsBookmakerId'],
            message: 'Bookmaker ID and label must both be set or both be null',
        });
    }
});
export const updateFixtureMappingSchema = fixtureMappingWriteSchema.partial();
export const verifierPolicySchema = z.object({
    id: idStringSchema,
    version: positiveIntSchema,
    isActive: z.boolean().default(false),
    minEdgeBps: basisPointsSchema.default(300),
    verificationWindowSeconds: positiveIntSchema.default(120),
    staleOddsSeconds: positiveIntSchema.default(90),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const createVerifierPolicySchema = verifierPolicySchema
    .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    isActive: z.boolean().default(false),
    minEdgeBps: basisPointsSchema.default(300),
    verificationWindowSeconds: positiveIntSchema.default(120),
    staleOddsSeconds: positiveIntSchema.default(90),
});
export const updateVerifierPolicySchema = createVerifierPolicySchema.partial();
export const verificationEvidenceSchema = z.object({
    polymarketProbabilityBps: basisPointsSchema.optional(),
    sportsSourceProbabilityBps: basisPointsSchema.optional(),
    edgeBps: z.number().int().min(-10000).max(10000).optional(),
    absEdgeBps: basisPointsSchema.optional(),
    edgeDirection: edgeDirectionSchema.optional(),
    providerTimestamp: timestampSchema.nullable().optional(),
    fetchTimestamp: timestampSchema.optional(),
    mappingSnapshot: jsonObjectSchema.nullable(),
    policySnapshot: jsonObjectSchema.nullable(),
    providerSnapshot: jsonObjectSchema.nullable(),
});
export const verificationAttemptSchema = z.object({
    id: idStringSchema.optional(),
    verificationAttemptId: idStringSchema,
    triggerId: idStringSchema,
    verifierState: verifierStateSchema,
    unverifiedReason: unverifiedReasonSchema.nullable().default(null),
    footballFixtureId: idStringSchema.nullable().optional(),
    fixtureMappingId: idStringSchema.nullable().optional(),
    policyId: idStringSchema.nullable().optional(),
    policyVersion: positiveIntSchema.nullable().optional(),
    rolloutGroup: nonEmptyStringSchema.nullable().optional(),
    fixtureStartTime: timestampSchema.nullable().optional(),
    oddsPhase: oddsPhaseSchema,
    evidence: verificationEvidenceSchema,
    createdAt: timestampSchema.optional(),
    updatedAt: timestampSchema.optional(),
    finalizedAt: timestampSchema.nullable().optional(),
});
export const internalWatcherConfigResponseSchema = z.object({
    generatedAt: timestampSchema,
    cacheTtlSeconds: positiveIntSchema.default(60),
    fixtures: z.array(footballFixtureSchema),
    mappings: z.array(fixtureMappingSchema),
    largeTradeTargets: z.array(fixtureMappingSchema).default([]),
    activePolicy: verifierPolicySchema,
});
export const adminReadinessWarningCodeSchema = z.enum([
    'missing_active_policy',
    'no_enabled_fixtures',
    'no_runtime_ready_equivalent_mappings',
    'enabled_mappings_on_archived_fixtures',
]);
export const adminReadinessResponseSchema = z.object({
    generatedAt: timestampSchema,
    activePolicyPresent: z.boolean(),
    enabledFixtureCount: nonNegativeIntSchema,
    runtimeReadyEquivalentMappingCount: nonNegativeIntSchema,
    enabledMappingsOnArchivedFixturesCount: nonNegativeIntSchema,
    warningCodes: z.array(adminReadinessWarningCodeSchema),
});
export const mappingCoverageStatusSchema = z.enum(['needs_review', 'candidate_created', 'linked', 'dismissed']);
export const mappingCoverageSignalSchema = z.object({
    coverageSignalId: idStringSchema,
    polymarketEventId: nonEmptyStringSchema,
    polymarketOutcomeId: nonEmptyStringSchema,
    marketTitle: nonEmptyStringSchema,
    outcomeLabel: nonEmptyStringSchema,
    whaleIntensity: z.number().int().min(0).max(100),
    priceMoveBps: z.number().int().min(-10000).max(10000),
    fixtureRelevanceScore: z.number().int().min(0).max(100).default(0),
    detectedAt: timestampSchema,
    mappingStatus: mappingCoverageStatusSchema.default('needs_review'),
    candidateId: idStringSchema.nullable().default(null),
    competitionCode: nonEmptyStringSchema.nullable().default(null),
    season: z.number().int().positive().nullable().default(null),
    footballFixtureId: idStringSchema.nullable().default(null),
    polymarketMarketId: idStringSchema.nullable().default(null),
    coverageReason: z.enum(['fixture_uncovered', 'unsupported_market', 'fixture_conflict']).nullable().default(null),
});
export const mappingCoverageQueueQuerySchema = z.object({
    mappingStatus: mappingCoverageStatusSchema.optional(),
    competitionCode: nonEmptyStringSchema.optional(),
    season: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().positive().max(100).optional().default(50),
});
export const markMappingCoverageSignalCandidateResponseSchema = mappingCoverageSignalSchema.extend({
    candidateId: idStringSchema,
    mappingStatus: z.literal('candidate_created'),
});
export const internalAttemptUpsertRequestSchema = verificationAttemptSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    finalizedAt: true,
});
